/* eslint-disable import/prefer-default-export */
import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import {
    CodeBlock,
    BlockType,
    SettingType,
    Define,
    ValidationMessageType,
    ValidationMessage,
    SettingState,
    Setting,
    TibboDataType,
} from '@appblocks/codegeneration/types';
import BaseNode from '@appblocks/codegeneration/Nodes/BaseNode';
import NodePort from '@appblocks/codegeneration/Nodes/NodePort';
import NodeParameter from '@appblocks/codegeneration/Nodes/NodeParameter';
import PlatformUtils from '@appblocks/platforms/PlatformUtils';


export const getCodeBlocks = (feature: BaseNode): CodeBlock[] => {
    const blocks: CodeBlock[] = [];
    const project: ProjectConfiguration = feature.project;
    const tibbit = project.getFeature(feature.getSettingValue('TIBBIT'));
    if (!tibbit) {
        return blocks;
    }
    const tibbitName = tibbit.getSettingValue('NAME');
    const functionName = `${tibbitName}_data_available_${feature.getID()}`;
    const SERNAME = `SER_${tibbit.getSettingValue('NAME')}`;
    const dataLocation = `${SERNAME}_data`;
    const format = feature.getSettingValue('FORMAT') || 'binary';

    blocks.push({
        location: 'FUNCTION_DEFINITIONS',
        blockType: BlockType.CODE,
        id: 'wiegand_process_input',
        template: `
function wiegandProcessInput(wiegand_data, format = 'binary') {
    let input = '';

    // First, normalize input to binary string
    if (format === 'binary') {
        input = wiegand_data.replace(/[^01]/g, ''); // Remove any non-binary characters
    } else if (format === 'hex') {
        input = BigInt('0x' + wiegand_data).toString(2).padStart(26, '0');
    } else if (format === 'dec') {
        input = BigInt(wiegand_data).toString(2).padStart(26, '0');
    } else {
        console.log('Unsupported format: must be "binary", "hex", or "decimal".');
        return;
    }

    const bitCount = input.length;
    let facilityCodeCount = 8; // Default to 8 bits

    switch (bitCount) {
        case 26: facilityCodeCount = 8; break;
        case 32: facilityCodeCount = 10; break;
        case 34: facilityCodeCount = 12; break;
        case 37: facilityCodeCount = 15; break;
        case 40: facilityCodeCount = 16; break;
        case 42: facilityCodeCount = 18; break;
        case 56: facilityCodeCount = 24; break;
        case 58: facilityCodeCount = 26; break;
        case 64: facilityCodeCount = 30; break;
        default:
            console.warn(\`Unknown bit length $\{bitCount}, assuming facility code size 8 bits.\`);
            break;
    }

    const facility_code = input.substring(1, 1 + facilityCodeCount); // mid(input, 2, facilityCodeCount)
    const card_code = input.substring(1 + facilityCodeCount, bitCount - 1); // mid(input, 2+facilityCodeCount, bitCount-facilityCodeCount-2)
    const id_code = input.substring(1, 1 + facilityCodeCount + card_code.length); // mid(input,2,facilityCodeCount+len(card_code))

        function convertBits(bits, format) {
        const num = BigInt('0b' + bits);
        const expected_hex_length = (bits.length + 3) / 4;
        if (format === 'binary') {
            return bits;
        } else if (format === 'hex') {
            return num.toString(16).toUpperCase().padStart(expected_hex_length, '0'); // Uppercase hex
        } else if (format === 'dec') {
            return num.toString(10);
        }
        return bits;
    }

    return {
        wiegand_data: convertBits(input, format),
        facility_code: convertBits(facility_code, format),
        card_code: convertBits(card_code, format),
        id_code: convertBits(id_code, format)
    };
}
`,
    });

    blocks.push({
        location: 'on_ser_data_arrival',
        blockType: BlockType.CODE,
        template: `
if (serialInterface === "${SERNAME}") {
    const input = data.toString();
    try {
        const { wiegand_data, facility_code, card_code, id_code } = wiegandProcessInput(input, "${format}");
        /****on_${SERNAME}_data_arrival****/
    } catch (error) {
        console.log('Error processing wiegand_data:');
        return;
    }
}
`,
    });

    const links = project.getNodeLinks(feature, 'data_available');
    if (links.length > 0) {
        let dataCode = '';
        const link = links[0];
        dataCode += `/****${link.getSourceId()}****/\r\n`;
        blocks.push({
            location: `on_${SERNAME}_data_arrival`,
            blockType: BlockType.CODE,
            template: `
${dataCode}
            `,
        });
    }

    return blocks;
};
